import { ArrowLeft, LogOut, Sprout } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BookMapPanel } from "../components/BookMapPanel";
import { LanguageToggle } from "../components/LanguageToggle";
import { LoginScreen } from "../components/LoginScreen";
import {
  captureNote,
  createLink,
  createRelation,
  createSource,
  deleteLink,
  archiveSource,
  loadBootstrap,
  loadLinks,
  loadSession,
  loginWithPasscode,
  loginWithCredentials,
  registerAccount,
  logoutSession,
  updateNote,
  updateSource
} from "../lib/api";
import { graphLinks, gardenMetrics, notes, sourceNodes } from "../lib/sampleData";
import type { EntityLink, ExtractionCandidate, GardenMetric, GraphLink, NoteCard, SourceKind, SourceNode } from "../types";

type Bootstrap = {
  sourceNodes: SourceNode[];
  notes: NoteCard[];
  gardenMetrics: GardenMetric[];
  graphLinks: GraphLink[];
  candidates?: ExtractionCandidate[];
  mode?: string;
};

export function Books() {
  const navigate = useNavigate();
  const { sourceId: routeSourceId } = useParams<{ sourceId?: string }>();
  const [data, setData] = useState<Bootstrap>({
    sourceNodes,
    notes,
    gardenMetrics,
    graphLinks,
    candidates: [],
    mode: "initial"
  });
  const [selectedId, setSelectedId] = useState(sourceNodes[0]?.id ?? "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [loginError, setLoginError] = useState("");
  const [sourceError, setSourceError] = useState("");
  const [captureError, setCaptureError] = useState("");
  const [linkError, setLinkError] = useState("");
  const [relationError, setRelationError] = useState("");
  const [linksByObject, setLinksByObject] = useState<Record<string, EntityLink[]>>({});

  const routeSelectedId = routeSourceId ? decodeURIComponent(routeSourceId) : "";

  const selectSource = useCallback(
    (id: string, options?: { replace?: boolean }) => {
      if (!id) {
        setSelectedId("");
        return;
      }

      setSelectedId(id);
      navigate(`/books/${encodeURIComponent(id)}`, { replace: options?.replace ?? false });
    },
    [navigate]
  );

  const resolveSelectedId = useCallback(
    (payload: Bootstrap) => {
      if (routeSelectedId && payload.sourceNodes.some((source) => source.id === routeSelectedId)) {
        return routeSelectedId;
      }

      return payload.sourceNodes[0]?.id ?? "";
    },
    [routeSelectedId]
  );

  useEffect(() => {
    let isMounted = true;

    loadSession()
      .then(async (session) => {
        if (!isMounted) {
          return;
        }

        if (session.authenticated) {
          const payload = await loadBootstrap();
          if (!isMounted) {
            return;
          }
          setData(payload);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsCheckingAuth(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || data.sourceNodes.length === 0) {
      return;
    }

    if (routeSelectedId && data.sourceNodes.some((source) => source.id === routeSelectedId)) {
      setSelectedId(routeSelectedId);
      return;
    }

    const fallbackId = data.sourceNodes[0]?.id ?? "";
    if (fallbackId) {
      selectSource(fallbackId, { replace: true });
    }
  }, [data.sourceNodes, isAuthenticated, routeSelectedId, selectSource]);

  const refreshData = async () => {
    const payload = await loadBootstrap();
    setData(payload);
    return payload;
  };

  const completeAuth = async () => {
    const payload = await refreshData();
    const nextId = resolveSelectedId(payload);
    if (nextId) {
      selectSource(nextId, { replace: true });
    }
    setIsAuthenticated(true);
  };

  const handleLoginCredentials = async (input: { email: string; password: string }) => {
    setLoginError("");
    try {
      await loginWithCredentials(input);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Đăng nhập thất bại");
      throw error;
    }
  };

  const handleRegister = async (input: { email: string; username: string; password: string }) => {
    setLoginError("");
    try {
      await registerAccount(input);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Đăng ký thất bại");
      throw error;
    }
  };

  const handleLoginPasscode = async (passcode: string) => {
    setLoginError("");
    try {
      await loginWithPasscode(passcode);
      await completeAuth();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : "Login failed");
      throw error;
    }
  };

  const handleLogout = async () => {
    await logoutSession();
    setIsAuthenticated(false);
  };

  const handleCreateSource = async (input: { title: string; kind: SourceKind; parentId?: string; properties?: Record<string, unknown> }) => {
    setSourceError("");

    try {
      const result = await createSource(input);
      const refreshed = await refreshData();
      const createdId = result.data?.sourceId ?? result.data?.id;
      const nextId = createdId ?? input.parentId ?? refreshed.sourceNodes[0]?.id ?? "";
      if (nextId) {
        selectSource(nextId);
      }
      return createdId as string | undefined;
    } catch (error) {
      setSourceError(error instanceof Error ? error.message : "Create source failed");
      throw error;
    }
  };

  const handleUpdateSource = async (
    id: string,
    input: { title?: string; kind?: SourceKind; progress?: number; parentId?: string | null; properties?: Record<string, unknown> }
  ) => {
    setSourceError("");

    try {
      await updateSource(id, input);
      await refreshData();
    } catch (error) {
      setSourceError(error instanceof Error ? error.message : "Update source failed");
      throw error;
    }
  };

  const handleArchiveSource = async (id: string) => {
    setSourceError("");

    try {
      await archiveSource(id);
      const refreshed = await refreshData();
      const nextId = refreshed.sourceNodes[0]?.id ?? "";
      if (nextId) {
        selectSource(nextId, { replace: true });
      }
    } catch (error) {
      setSourceError(error instanceof Error ? error.message : "Archive source failed");
      throw error;
    }
  };

  const handleCapture = async (input: {
    kind: NoteCard["kind"];
    content: string;
    sourceId: string;
    relationType?: string;
    relationTargetId?: string;
    properties?: Record<string, unknown>;
  }) => {
    setCaptureError("");

    try {
      const result = await captureNote(input);
      await refreshData();
      selectSource(input.sourceId, { replace: true });
      return result.data as { objectId?: string } | undefined;
    } catch (error) {
      setCaptureError(error instanceof Error ? error.message : "Capture failed");
      throw error;
    }
  };

  const handleUpdateNote = async (id: string, input: { content: string }) => {
    setCaptureError("");

    try {
      await updateNote(id, input);
      await refreshData();
    } catch (error) {
      setCaptureError(error instanceof Error ? error.message : "Update note failed");
      throw error;
    }
  };

  const handleLoadLinks = async (objectId: string) => {
    setLinkError("");

    try {
      const payload = await loadLinks(objectId);
      setLinksByObject((current) => ({ ...current, [objectId]: payload.data ?? [] }));
    } catch (error) {
      setLinkError(error instanceof Error ? error.message : "Load links failed");
      throw error;
    }
  };

  const handleCreateLink = async (objectId: string, input: { url: string; label?: string }) => {
    setLinkError("");

    try {
      await createLink({ objectId, ...input });
      await handleLoadLinks(objectId);
    } catch (error) {
      setLinkError(error instanceof Error ? error.message : "Create link failed");
      throw error;
    }
  };

  const handleDeleteLink = async (objectId: string, linkId: string) => {
    setLinkError("");

    try {
      await deleteLink(linkId);
      await handleLoadLinks(objectId);
    } catch (error) {
      setLinkError(error instanceof Error ? error.message : "Delete link failed");
      throw error;
    }
  };

  const handleCreateRelation = async (input: {
    sourceObjectId: string;
    relationType: string;
    targetObjectId: string;
    properties?: Record<string, unknown>;
  }) => {
    setRelationError("");

    try {
      const result = await createRelation(input);
      await refreshData();
      if (result.mode === "existing") {
        setRelationError("Kết nối này đã tồn tại.");
      }
    } catch (error) {
      setRelationError(error instanceof Error ? error.message : "Create relation failed");
      throw error;
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper text-sm font-bold text-muted">
        Loading Books
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <LoginScreen
        onLoginCredentials={handleLoginCredentials}
        onRegister={handleRegister}
        onLoginPasscode={handleLoginPasscode}
        error={loginError}
      />
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="sticky top-0 z-20 border-b border-line bg-white/95 backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-[1560px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-brand">
              <Sprout size={21} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-base font-bold leading-tight">Human Note Books</p>
              <p className="hidden truncate text-xs font-medium text-muted sm:block">Nạp cây sách, sửa mục lục, ghi note theo từng mục</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <LanguageToggle />
            <button
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
              type="button"
              onClick={() => navigate("/?tab=library")}
            >
              <ArrowLeft size={17} aria-hidden="true" />
              <span className="hidden sm:inline">Thư viện</span>
            </button>
            <button
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-white text-slate-600 hover:bg-slate-50"
              type="button"
              aria-label="Sign out"
              onClick={handleLogout}
            >
              <LogOut size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1560px] px-4 py-4 sm:px-6">
        <BookMapPanel
          sources={data.sourceNodes}
          notes={data.notes}
          selectedSourceId={selectedId}
          onSelectSource={selectSource}
          onCreateSource={handleCreateSource}
          onUpdateSource={handleUpdateSource}
          onArchiveSource={handleArchiveSource}
          onCapture={handleCapture}
          onUpdateNote={handleUpdateNote}
          onCreateLink={handleCreateLink}
          onLoadLinks={handleLoadLinks}
          onDeleteLink={handleDeleteLink}
          linksByObject={linksByObject}
          onCreateRelation={handleCreateRelation}
          sourceError={sourceError}
          captureError={captureError}
          linkError={linkError}
          relationError={relationError}
        />
      </main>
    </div>
  );
}
